import React from 'react';
import { Separator } from '../../../components/ui/separator';
import type { PipelineSummary } from '../lib/pipelineSummary';
import { cn } from '@/lib/utils';

const money = (n: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(n);

const percent = (rate: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(rate);

interface PipelineSummaryBarProps {
  summary: PipelineSummary;
}

const PipelineSummaryBar: React.FC<PipelineSummaryBarProps> = ({ summary }) => {
  const totalWeight = summary.segments.reduce((s, seg) => s + seg.weight, 0);

  const metrics = [
    {
      key: 'pipeline',
      label: 'Pipeline',
      labelDesktop: 'Total pipeline',
      value: money(summary.totalPipeline),
      valueClass: 'text-primary',
    },
    {
      key: 'count',
      label: 'Negócios',
      labelDesktop: 'Negociações',
      value: String(summary.negotiationsCount),
      valueClass: 'text-default',
    },
    {
      key: 'won',
      label: 'Ganho',
      labelDesktop: 'Ganho',
      value: money(summary.wonValue),
      valueClass: 'text-success',
    },
    {
      key: 'conversion',
      label: 'Conv.',
      labelDesktop: 'Taxa de conversão',
      value: percent(summary.conversionRate),
      valueClass: 'text-default',
    },
  ] as const;

  return (
    <section
      aria-label="Resumo do pipeline"
      className="flex min-w-0 flex-col gap-s2 border-b border-border bg-surface-1 px-s4 py-s3 md:flex-row md:items-center md:gap-s6 md:bg-surface-0 md:px-s6 md:py-s2"
    >
      <ul
        className={cn(
          'm-0 grid w-full min-w-0 list-none grid-cols-4 gap-s1',
          'md:flex md:w-auto md:flex-wrap md:items-center md:gap-x-s6 md:gap-y-s2'
        )}
      >
        {metrics.map((metric, index) => (
          <li
            key={metric.key}
            className="flex min-w-0 flex-col gap-s1 overflow-hidden md:flex-row md:items-center md:gap-s2"
          >
            {index > 0 ? (
              <Separator orientation="vertical" className="mr-s2 hidden h-5 md:block" />
            ) : null}
            <span className="metric-label truncate text-2xs text-muted md:text-xs">
              <span className="md:hidden">{metric.label}</span>
              <span className="hidden md:inline">{metric.labelDesktop}</span>
            </span>
            <span
              className={cn(
                'max-w-full truncate font-mono text-xs font-semibold tabular-nums tracking-tight sm:text-sm',
                metric.valueClass
              )}
            >
              {metric.value}
            </span>
          </li>
        ))}
      </ul>

      {totalWeight > 0 ? (
        <div
          className="flex h-1.5 min-w-0 flex-1 gap-s1 overflow-hidden rounded-full"
          role="img"
          aria-label="Distribuição do pipeline por etapa"
        >
          {summary.segments.map((seg, index) => {
            const pct = (seg.weight / totalWeight) * 100;
            const opacity =
              seg.tone === 'success'
                ? 1
                : 0.25 + (index / Math.max(summary.segments.length - 1, 1)) * 0.6;
            return (
              <div
                key={seg.id}
                className={
                  seg.tone === 'success'
                    ? 'h-full rounded-full bg-success'
                    : 'h-full rounded-full bg-primary'
                }
                style={{ width: `${pct}%`, opacity }}
              />
            );
          })}
        </div>
      ) : null}
    </section>
  );
};

export default PipelineSummaryBar;
